import ConfirmationDialog from '@components/common/ConfirmationDialog';
import { CustomTooltip } from '@components/shared/CustomTooltip';
import TeamMembersModal from '@components/TeamMemberModal';
import { useDeleteInterviews, useDeleteSurvey, useRemoveMember } from '@hooks/deleteQueries';
import { useProfile, useRecentSurveys, useWorkspaceUsage } from '@hooks/getQueries';
import {
  useDuplicateInterview,
  useDuplicateSurvey,
  useStopCollectingResponsesInterview,
  useStopCollectingResponsesSurvey,
} from '@hooks/postQueries';
import { useUpdateInterviewFinalize, useUpdateSurveyStatus } from '@hooks/putQueries';
import useConfirmationDialog from '@hooks/useDeleteConfirmation';
import useWorkspace from '@hooks/useWorkspace';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  alpha,
  Box,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Select,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { DataGrid } from '@mui/x-data-grid';
import { getStatusColor, getUserAccessType } from '@utils/colorUtils';
import { formatDate } from '@utils/dateFormat';
import formatString from '@utils/formatString';
import { useCallback, useState, useEffect } from 'react';
import { useNavigate } from 'react-router';
import {
  Calendar,
  Crown,
  Eye,
  Grid3X3,
  Info,
  LayoutList,
  Mic,
  ClipboardList,
  PenSquare,
  Shield,
  User,
} from 'lucide-react';
import DeleteConfirmationDialog from '../DeleteConfirmation';
import SurveyShare from '../dynamic-survey/SurveyShare';
import { CustomNoRowsOverlay } from '../shared/CustomNoRowsOverlay';
import StudyCard from './StudyCard';
import StudyInfoDialog from './StudyInfoDialog';

const useStyles = makeStyles((theme) => ({
  emptyList: {
    minHeight: 300,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: { fontWeight: 500 },
  actionButton: {
    cursor: 'pointer',
    width: '100%',
    '&:hover $title': {
      color: theme.palette.primary.main,
    },
  },
  dot: {
    height: 8,
    width: 8,
    margin: '0 5px',
    borderRadius: '50%',
    display: 'inline-block',
    backgroundColor: '#D2D2D2',
  },
}));

// Access type icons mapping
const ACCESS_ICONS = {
  Owner: Crown,
  Admin: Shield,
  Editor: PenSquare,
  Viewer: Eye,
};

// Mini progress bar for table view
function MiniProgressBar({ value, max, showInfinity = false }) {
  const theme = useTheme();
  const percentage = showInfinity ? Math.min(value * 3, 100) : max > 0 ? (value / max) * 100 : 0;

  const getColor = () => {
    if (percentage >= 90) return theme.palette.error.main;
    if (percentage >= 70) return theme.palette.warning.main;
    return theme.palette.primary.main;
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
      <Typography variant="body2" fontWeight={500} sx={{ minWidth: 50 }}>
        {value}/{showInfinity ? '∞' : max}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: 6,
          borderRadius: 3,
          backgroundColor: alpha(getColor(), 0.12),
          overflow: 'hidden',
          maxWidth: 60,
        }}
      >
        <Box
          sx={{
            height: '100%',
            width: `${Math.min(percentage, 100)}%`,
            borderRadius: 3,
            backgroundColor: getColor(),
            transition: 'width 0.4s ease-out',
          }}
        />
      </Box>
    </Box>
  );
}

export default function RecentStudies() {
  const classes = useStyles();
  const theme = useTheme();
  const { activeWorkspaceId } = useWorkspace();
  const { data: usageData } = useWorkspaceUsage(activeWorkspaceId);
  const remainingInterviewEntitlements = usageData?.entitlements?.ai_interviews_remaining;
  const { data: studies } = useRecentSurveys();
  const [openShare, setOpenShare] = useState(false);
  const [openTeamShare, setOpenTeamShare] = useState(false);
  const [studyId, setStudyId] = useState(null);
  const navigate = useNavigate();
  const [deleteSurveyText, setDeleteSurveyText] = useState(null);
  const { mutate: deleteSurvey, isPending: isSurveyDeleting } = useDeleteSurvey();
  const { mutate: deleteInterview, isPending: isInterviewDeleting } = useDeleteInterviews();
  const { data: userData } = useProfile();
  const { mutate: updateInterviewStatus } = useUpdateInterviewFinalize();
  const { mutate: updateSurveyStatus } = useUpdateSurveyStatus();
  const { mutate: leaveStudy } = useRemoveMember();
  const { mutate: duplicateSurvey, isPending: isDuplicatingSurvey } = useDuplicateSurvey();
  const { mutate: duplicateInterview, isPending: isDuplicatingInterview } = useDuplicateInterview();
  const { mutate: stopCollectingResponsesSurvey } = useStopCollectingResponsesSurvey();
  const { mutate: stopCollectingResponsesInterview } = useStopCollectingResponsesInterview();
  const { open, show, handleConfirm, handleClose } = useConfirmationDialog();

  // View mode: 'table' or 'card'
  const [viewMode, setViewMode] = useState(() => {
    return localStorage.getItem('studiesViewMode') || 'table';
  });

  // Card pagination
  const [cardsPerPage, setCardsPerPage] = useState(() => {
    return parseInt(localStorage.getItem('studiesCardsPerPage') || '8', 10);
  });
  const [cardPage, setCardPage] = useState(0);

  // Info dialog state
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [infoStudy, setInfoStudy] = useState(null);

  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedStudy, setSelectedStudy] = useState(null);
  const [showEditConfirm, setShowEditConfirm] = useState(false);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [duplicateStudyId, setDuplicateStudyId] = useState(null);
  const [duplicateStudyType, setDuplicateStudyType] = useState(null);
  const [editStudyId, setEditStudyId] = useState(null);

  // Persist view mode and cards per page
  useEffect(() => {
    localStorage.setItem('studiesViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('studiesCardsPerPage', cardsPerPage.toString());
    setCardPage(0); // Reset to first page when changing items per page
  }, [cardsPerPage]);

  const getDisabledState = useCallback(
    (row) => {
      const isOwner = row?.owner_id === userData?.id;
      const hasEditPermission = row?.members
        ?.find((member) => member?.user_email === userData?.email)
        ?.permissions?.includes('edit');

      const isArchived = row?.status === 'archived';
      const isInactive = row?.status === 'inactive';
      const isDraft = row?.status === 'draft';
      const isUploaded = row?.is_primary === false;

      return {
        edit: !(isOwner || hasEditPermission) || isArchived || isUploaded,
        preview: isArchived || isInactive || isDraft || isUploaded,
        shareWithParticipants: isArchived || isInactive || isDraft || isUploaded,
        addCollaborators: !(isOwner || hasEditPermission) || isArchived,
        stopCollectingResponses: !(isOwner || hasEditPermission) || isUploaded,
        archive: !(isOwner || hasEditPermission),
        delete: !isOwner,
        _isUploaded: isUploaded,
        _isArchived: isArchived,
        _isInactive: isInactive,
        _isDraft: isDraft,
        _hasPermission: isOwner || hasEditPermission,
      };
    },
    [userData],
  );

  const getDisabledTooltip = useCallback(
    (row, action) => {
      const state = getDisabledState(row);
      if (!state?.[action]) return '';

      const isUploaded = state._isUploaded;
      const isArchived = state._isArchived;
      const isInactive = state._isInactive;
      const isDraft = state._isDraft;
      const hasPermission = state._hasPermission;

      if (
        isUploaded &&
        ['edit', 'preview', 'shareWithParticipants', 'stopCollectingResponses'].includes(action)
      ) {
        return `Not available for uploaded ${row?.type?.toLowerCase() || 'study'}s`;
      }

      if (isArchived) {
        if (action === 'edit') return 'Cannot edit archived studies';
        if (['preview', 'shareWithParticipants'].includes(action)) return 'Study is archived';
      }

      if (isInactive || isDraft) {
        if (action === 'preview')
          return isDraft ? 'Complete study setup first' : 'Study is inactive';
        if (action === 'shareWithParticipants')
          return isDraft ? 'Complete study setup first' : 'Study is inactive';
      }

      if (!hasPermission) {
        if (['edit', 'addCollaborators', 'stopCollectingResponses', 'archive'].includes(action)) {
          return 'You do not have permission for this action';
        }
      }

      if (action === 'delete' && row?.owner_id !== userData?.id) {
        return 'Only the owner can delete this study';
      }

      return '';
    },
    [getDisabledState, userData],
  );

  const handleClick = (event, study) => {
    setAnchorEl(event.currentTarget);
    setSelectedStudy(study);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleOpenInfo = (study) => {
    setInfoStudy(study);
    setInfoDialogOpen(true);
  };

  // Card navigation handler - navigates to dashboard (main action)
  const handleCardNavigate = (study) => {
    if (study?.type === 'Survey') {
      navigate(`/dynamic-survey/${study?.id}/response-insight`);
    } else if (study?.type === 'Interview') {
      navigate(`/interview/${study?.id}/response-insight`);
    }
  };

  const handleDuplicateCancel = () => {
    setShowDuplicateConfirm(false);
    setDuplicateStudyId(null);
    setDuplicateStudyType(null);
  };

  const handleDuplicateConfirm = () => {
    if (duplicateStudyId && duplicateStudyType) {
      if (duplicateStudyType === 'Survey') {
        duplicateSurvey(duplicateStudyId, {
          onSuccess: () => {
            setShowDuplicateConfirm(false);
            setDuplicateStudyId(null);
            setDuplicateStudyType(null);
          },
        });
      } else {
        duplicateInterview(duplicateStudyId, {
          onSuccess: () => {
            setShowDuplicateConfirm(false);
            setDuplicateStudyId(null);
            setDuplicateStudyType(null);
          },
        });
      }
    }
  };

  const handleDelete = () => {
    if (selectedStudy?.type) {
      if (selectedStudy?.type === 'Survey') {
        show(() => deleteSurvey(selectedStudy?.id, isSurveyDeleting));
      } else {
        show(() => deleteInterview(selectedStudy?.id, isInterviewDeleting));
      }
    }
    handleCloseMenu();
  };

  const handleSendToAIParticipant = () => {
    setOpenShare(true);
  };

  const handleEditConfirm = () => {
    if (selectedStudy?.type === 'Survey') {
      navigate(`/dynamic-survey/${editStudyId}/edit`, {
        state: {
          id: editStudyId,
          totalResponses: selectedStudy?.total_responses || selectedStudy?.response || 0,
        },
      });
    } else {
      navigate(`/interview/edit/${editStudyId}#basic-info`, {
        state: {
          id: editStudyId,
          totalResponses: selectedStudy?.total_responses || selectedStudy?.response || 0,
        },
      });
    }
    setShowEditConfirm(false);
  };

  const handleEditCancel = () => {
    setShowEditConfirm(false);
    setEditStudyId(null);
  };

  const handleEdit = (study = selectedStudy) => {
    if (study?.status === 'created' || study?.status === 'draft') {
      if (study?.type === 'Survey') {
        navigate(`/dynamic-survey/${study?.id}/edit`, {
          state: {
            id: study?.id,
            totalResponses: study?.total_responses || study?.response || 0,
          },
        });
      } else {
        navigate(`/interview/edit/${study?.id}#basic-info`, {
          state: {
            id: study?.id,
            totalResponses: study?.total_responses || study?.response || 0,
          },
        });
      }
    } else {
      setSelectedStudy(study);
      setEditStudyId(study?.id);
      setShowEditConfirm(true);
    }
    handleCloseMenu();
  };

  const handlePreview = (study = selectedStudy) => {
    window.open(`${study?.study_link}&preview=true`, '_blank');
    handleCloseMenu();
  };

  const handleShare = (study = selectedStudy) => {
    setSelectedStudy(study);
    setOpenShare(true);
    handleCloseMenu();
  };

  const handleStatusChangeArchive = (study) => {
    const newStatus = study?.status === 'archived' ? 'inactive' : 'archived';
    const transformedData = { status: newStatus };

    if (study?.type === 'Interview') {
      updateInterviewStatus({ id: study.id, transformedData });
    } else if (study?.type === 'Survey') {
      updateSurveyStatus({ id: study.id, transformedData });
    }
    handleCloseMenu();
  };

  const handleStopCollectingResponse = (study) => {
    if (study?.type === 'Survey') {
      stopCollectingResponsesSurvey(
        { survey_id: study.id, disable_reason: 'user_requested' },
        { onSuccess: () => handleCloseMenu() },
      );
    } else if (study?.type === 'Interview') {
      stopCollectingResponsesInterview(
        { interview_id: study.id, disable_reason: 'user_requested' },
        { onSuccess: () => handleCloseMenu() },
      );
    }
  };

  // Card view action handlers
  const handleCardMoreActions = (study, action) => {
    setSelectedStudy(study);
    switch (action) {
      case 'duplicate':
        setDuplicateStudyId(study?.id);
        setDuplicateStudyType(study?.type);
        setShowDuplicateConfirm(true);
        break;
      case 'collaborators':
        setStudyId(study?.id);
        setOpenTeamShare(true);
        break;
      case 'stop':
        handleStopCollectingResponse(study);
        break;
      case 'archive':
        handleStatusChangeArchive(study);
        break;
      case 'delete':
        show(() => {
          if (study?.type === 'Survey') {
            deleteSurvey(study?.id);
          } else {
            deleteInterview(study?.id);
          }
        });
        break;
    }
  };

  const statusTooltips = {
    active: 'Participants are actively responding to the study.',
    draft: 'The study has been created but is not yet set up.',
    created: "The study is set up but hasn't started collecting responses yet.",
    inactive: 'The study has ended. Participants cannot submit responses.',
    archived: 'The study is fully completed and locked.',
    default: 'Status unknown.',
  };

  const hasStudies = studies && studies?.length > 0;

  // Table columns - Enhanced version
  const columns = hasStudies
    ? [
        {
          field: 'title',
          headerName: 'Study',
          minWidth: 300,
          flex: 1,
          headerClassName: 'super-app-theme--header',
          renderCell: (params) => {
            const isInterview = params?.row?.type === 'Interview';
            const maxParticipants = params?.row?.max_participants || 0;
            const showEntitlementWarning =
              isInterview &&
              typeof remainingInterviewEntitlements === 'number' &&
              maxParticipants > remainingInterviewEntitlements;

            return (
              <Box
                className={classes.actionButton}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}
              >
                {/* Type Icon */}
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    backgroundColor: alpha(
                      isInterview ? theme.palette.info.main : theme.palette.success.main,
                      0.1,
                    ),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {isInterview ? (
                    <Mic size={18} color={theme.palette.info.main} />
                  ) : (
                    <ClipboardList size={18} color={theme.palette.success.main} />
                  )}
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant="body2"
                    title={params?.row?.title}
                    onClick={() => {
                      if (params?.row?.type === 'Survey') {
                        navigate(`/dynamic-survey/${params?.row?.id}/response-insight`);
                      }
                      if (params?.row?.type === 'Interview') {
                        navigate(`/interview/${params?.row?.id}/response-insight`);
                      }
                    }}
                    className={classes.title}
                    sx={{
                      fontWeight: 500,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {params.row.title}
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#545454' }}>
                    {params.row.is_primary ? 'Primary' : 'Secondary'}
                    {showEntitlementWarning && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{ color: 'warning.main', ml: 1 }}
                      >
                        • Entitlement warning
                      </Typography>
                    )}
                  </Typography>
                </Box>
              </Box>
            );
          },
        },
        {
          field: 'status',
          headerName: 'Status',
          width: 80,
          headerClassName: 'super-app-theme--header',
          renderCell: (params) => {
            const { status } = params.row;
            const tooltipText = statusTooltips[status] || statusTooltips.default;
            const statusColor = getStatusColor(status);

            return (
              <CustomTooltip title={`${formatString(status)}: ${tooltipText}`} arrow>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      backgroundColor: statusColor,
                      boxShadow: `0 0 6px ${alpha(statusColor, 0.5)}`,
                      animation: status === 'active' ? 'pulse 2s infinite' : 'none',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.6 },
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {formatString(status)}
                  </Typography>
                </Box>
              </CustomTooltip>
            );
          },
        },
        {
          field: 'user_access_type',
          headerName: 'Access',
          width: 80,
          headerClassName: 'super-app-theme--header',
          renderCell: (params) => {
            const accessType = params?.row?.user_access_type;
            const accessInfo = getUserAccessType(accessType);
            const Icon = ACCESS_ICONS[accessType] || User;

            return (
              <CustomTooltip title={accessInfo?.label || accessType} arrow>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: 1,
                    backgroundColor: alpha(accessInfo?.backgroundColor || '#ccc', 0.3),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon size={16} color={accessInfo?.color || '#666'} />
                </Box>
              </CustomTooltip>
            );
          },
        },
        {
          field: 'response',
          headerName: 'Responses',
          minWidth: 140,
          headerClassName: 'super-app-theme--header',
          renderCell: (params) => {
            const isSurvey = params?.row?.type === 'Survey';
            const maxParticipants = params?.row?.max_participants || 0;
            const responses = params?.row?.response || 0;

            return (
              <MiniProgressBar
                value={responses}
                max={isSurvey ? maxParticipants : (maxParticipants || 0)}
                showInfinity={isSurvey}
              />
            );
          },
        },
        {
          field: 'created_at',
          headerName: 'Created',
          minWidth: 100,
          headerClassName: 'super-app-theme--header',
          renderCell: (params) => {
            const createdAt = params?.row?.created_at;
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Calendar size={14} color="#888" />
                <Typography variant="body2" color="text.secondary">
                  {createdAt ? formatDate(createdAt) : '—'}
                </Typography>
              </Box>
            );
          },
        },
        {
          field: 'end_date',
          headerName: 'End Date',
          minWidth: 100,
          headerClassName: 'super-app-theme--header',
          renderCell: (params) => {
            const endDate = params?.row?.end_date || params?.row?.expires_at;
            const isExpired = endDate && new Date(endDate) < new Date();

            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Calendar size={14} color={isExpired ? theme.palette.error.main : '#888'} />
                <Typography variant="body2" color={isExpired ? 'error.main' : 'text.secondary'}>
                  {endDate ? formatDate(endDate) : '—'}
                </Typography>
              </Box>
            );
          },
        },
        {
          field: 'info',
          headerName: '',
          width: 50,
          sortable: false,
          filterable: false,
          headerClassName: 'super-app-theme--header',
          renderCell: (params) => (
            <Tooltip title="Quick Info">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleOpenInfo(params.row);
                }}
              >
                <Info size={16} />
              </IconButton>
            </Tooltip>
          ),
        },
        {
          field: 'actions',
          headerName: '',
          width: 50,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          align: 'center',
          headerClassName: 'super-app-theme--header stickyHeader',
          cellClassName: 'stickyColumn',
          renderCell: (params) => (
            <IconButton
              className={classes.actionButton}
              onClick={(event) => handleClick(event, params.row)}
            >
              <MoreHorizIcon />
            </IconButton>
          ),
        },
      ]
    : [];

  // Pagination calculations for card view
  const totalStudies = studies?.length || 0;
  const totalCardPages = Math.ceil(totalStudies / cardsPerPage);
  const paginatedStudies = studies?.slice(cardPage * cardsPerPage, (cardPage + 1) * cardsPerPage);

  return (
    <Box
      px={{ xs: 2, sm: 3 }}
      py={{ xs: 2, sm: 3 }}
      pb={{ xs: 4, sm: 5 }}
      data-tour="recent-studies"
    >
      {/* Header with View Toggle */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Typography variant="subtitle2">Recent Studies</Typography>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, value) => value && setViewMode(value)}
          size="small"
          sx={{
            '& .MuiToggleButton-root': {
              border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              px: 1.5,
              py: 0.5,
              '&.Mui-selected': {
                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
              },
            },
          }}
        >
          <ToggleButton value="table">
            <LayoutList size={16} />
          </ToggleButton>
          <ToggleButton value="card">
            <Grid3X3 size={16} />
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Card View */}
      {viewMode === 'card' && (
        <>
          <Grid container spacing={2}>
            {paginatedStudies?.map((study) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={study.id}>
                <StudyCard
                  study={study}
                  onEdit={handleEdit}
                  onPreview={handlePreview}
                  onShare={handleShare}
                  onInfo={handleOpenInfo}
                  onNavigate={handleCardNavigate}
                  onMoreActions={handleCardMoreActions}
                  disabledState={getDisabledState(study)}
                  getDisabledTooltip={getDisabledTooltip}
                />
              </Grid>
            ))}
            {(!studies || studies.length === 0) && (
              <Grid item xs={12}>
                <Box
                  sx={{
                    minHeight: 200,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: `1px dashed ${theme.palette.divider}`,
                    borderRadius: 2,
                  }}
                >
                  <CustomNoRowsOverlay
                    title="No studies yet"
                    text="Get started by creating a new study."
                  />
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Card View Pagination */}
          {totalStudies > 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 2,
                mt: 2,
                pt: 2,
                borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Cards per page:
                </Typography>
                <FormControl size="small" sx={{ minWidth: 60 }}>
                  <Select
                    value={cardsPerPage}
                    onChange={(e) => setCardsPerPage(e.target.value)}
                    sx={{ fontSize: '0.75rem', '& .MuiSelect-select': { py: 0.5, px: 1 } }}
                  >
                    <MenuItem value={4}>4</MenuItem>
                    <MenuItem value={8}>8</MenuItem>
                    <MenuItem value={12}>12</MenuItem>
                    <MenuItem value={16}>16</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {cardPage * cardsPerPage + 1}–
                {Math.min((cardPage + 1) * cardsPerPage, totalStudies)} of {totalStudies}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <IconButton
                  size="small"
                  disabled={cardPage === 0}
                  onClick={() => setCardPage((p) => p - 1)}
                >
                  <Typography variant="caption">‹</Typography>
                </IconButton>
                <IconButton
                  size="small"
                  disabled={cardPage >= totalCardPages - 1}
                  onClick={() => setCardPage((p) => p + 1)}
                >
                  <Typography variant="caption">›</Typography>
                </IconButton>
              </Box>
            </Box>
          )}
        </>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <Box
          sx={{
            '& .super-app-theme--header': {
              fontWeight: 600,
              backgroundColor: '#F2F2F2',
              padding: '10px !important',
            },
            '& .MuiDataGrid-cell': {
              padding: '10px !important',
            },
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 600,
            },
            '& .MuiDataGrid-withBorderColor': {
              borderColor: hasStudies ? '#E8E8E8 !important' : 'none',
            },
            '& .MuiDataGrid-columnHeaders': {
              display: hasStudies ? 'flex' : 'none',
            },
            '& .stickyHeader': {
              position: 'sticky !important',
              top: 0,
              zIndex: 1,
            },
            '& .stickyColumn': {
              position: 'sticky',
              right: 0,
              zIndex: 1,
              backgroundColor: '#FFFFFF',
              borderLeft: '1px solid #E8E8E8',
            },
          }}
        >
          <DataGrid
            rows={studies || []}
            disableColumnMenu
            rowHeight={64}
            columns={columns}
            pageSize={5}
            rowsPerPageOptions={[5]}
            disableRowSelectionOnClick
            sx={{
              height: hasStudies ? 'auto' : 500,
              backgroundColor: 'background.default',
              border: '1px solid #E8E8E8',
            }}
            slots={{
              noRowsOverlay: () => (
                <CustomNoRowsOverlay
                  title="No studies yet"
                  text="Get started by creating a new study."
                />
              ),
            }}
            hideFooter
          />
        </Box>
      )}

      {/* Study Info Dialog */}
      <StudyInfoDialog
        open={infoDialogOpen}
        onClose={() => setInfoDialogOpen(false)}
        study={infoStudy}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleConfirm}
        isPending={isSurveyDeleting || isInterviewDeleting}
        title="Delete Study"
        disabled={deleteSurveyText !== selectedStudy?.title}
        okText={isSurveyDeleting || isInterviewDeleting ? 'Deleting...' : 'Delete Study'}
        content={
          <Box>
            <Typography>This will permanently delete the study.</Typography>
            <Typography variant="subtitle2">{selectedStudy?.title}</Typography>
            <Box mt={1}>
              <Typography mb={0.5}>To confirm, please enter the title of the study</Typography>
              <TextField fullWidth onChange={(e) => setDeleteSurveyText(e.target.value)} />
            </Box>
          </Box>
        }
      />

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        elevation={0}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Tooltip title={getDisabledTooltip(selectedStudy, 'edit')} placement="left">
          <span>
            <MenuItem onClick={() => handleEdit()} disabled={getDisabledState(selectedStudy)?.edit}>
              Edit
            </MenuItem>
          </span>
        </Tooltip>
        <MenuItem
          onClick={() => {
            setDuplicateStudyId(selectedStudy?.id);
            setDuplicateStudyType(selectedStudy?.type);
            setShowDuplicateConfirm(true);
            handleCloseMenu();
          }}
          disabled={
            (selectedStudy?.type === 'Survey' && isDuplicatingSurvey) ||
            (selectedStudy?.type === 'Interview' && isDuplicatingInterview)
          }
        >
          Duplicate
        </MenuItem>
        <Tooltip title={getDisabledTooltip(selectedStudy, 'preview')} placement="left">
          <span>
            <MenuItem
              onClick={() => handlePreview()}
              disabled={getDisabledState(selectedStudy)?.preview}
            >
              Preview
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip
          title={getDisabledTooltip(selectedStudy, 'shareWithParticipants')}
          placement="left"
        >
          <span>
            <MenuItem
              onClick={handleSendToAIParticipant}
              disabled={getDisabledState(selectedStudy)?.shareWithParticipants}
            >
              Share with Participants
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getDisabledTooltip(selectedStudy, 'addCollaborators')} placement="left">
          <span>
            <MenuItem
              onClick={() => {
                handleCloseMenu();
                setStudyId(selectedStudy?.id);
                setSelectedStudy(selectedStudy);
                setOpenTeamShare(true);
              }}
              disabled={getDisabledState(selectedStudy)?.addCollaborators}
            >
              Manage Collaborators
            </MenuItem>
          </span>
        </Tooltip>
        <Divider />
        <Tooltip
          title={
            ['Owner', 'Admin'].includes(selectedStudy?.user_access_type)
              ? 'Owners and admins cannot leave the study'
              : ''
          }
          placement="left"
        >
          <span>
            <MenuItem
              onClick={() => {
                leaveStudy({
                  surveyId: selectedStudy?.id,
                  email: userData?.email,
                  study_type: selectedStudy?.type === 'Survey' ? 'survey' : 'interview',
                });
              }}
              disabled={['Owner', 'Admin'].includes(selectedStudy?.user_access_type)}
            >
              Leave Study
            </MenuItem>
          </span>
        </Tooltip>
        {selectedStudy?.status === 'active' && (
          <Tooltip
            title={getDisabledTooltip(selectedStudy, 'stopCollectingResponses')}
            placement="left"
          >
            <span>
              <MenuItem
                onClick={() => handleStopCollectingResponse(selectedStudy)}
                disabled={getDisabledState(selectedStudy)?.stopCollectingResponses}
              >
                Stop collecting responses
              </MenuItem>
            </span>
          </Tooltip>
        )}
        <Tooltip title={getDisabledTooltip(selectedStudy, 'archive')} placement="left">
          <span>
            <MenuItem
              onClick={() => handleStatusChangeArchive(selectedStudy)}
              disabled={getDisabledState(selectedStudy)?.archive}
            >
              {selectedStudy?.status === 'archived' ? 'Unarchive' : 'Archive'}
            </MenuItem>
          </span>
        </Tooltip>
        <Tooltip title={getDisabledTooltip(selectedStudy, 'delete')} placement="left">
          <span>
            <MenuItem
              onClick={handleDelete}
              sx={{ color: 'error.main' }}
              disabled={getDisabledState(selectedStudy)?.delete}
            >
              Delete
            </MenuItem>
          </span>
        </Tooltip>
      </Menu>

      {/* Share Dialog */}
      <SurveyShare
        study_link={selectedStudy?.short_url || selectedStudy?.study_link}
        qr_code_url={selectedStudy?.qr_code_url}
        open={openShare}
        hideSendToAI={selectedStudy?.type === 'Survey' ? false : true}
        handleClose={() => setOpenShare(false)}
        studyId={selectedStudy?.id}
      />

      {/* Team Members Modal */}
      {studyId && (
        <TeamMembersModal
          showPreviewAvatars={false}
          open={openTeamShare}
          type={selectedStudy?.type === 'Interview' ? 'interview' : 'survey'}
          onClose={() => {
            setOpenTeamShare(false);
            setStudyId(null);
          }}
          studyId={studyId}
        />
      )}

      {/* Edit Confirmation Dialog */}
      <DeleteConfirmationDialog
        open={showEditConfirm}
        onClose={handleEditCancel}
        onConfirm={handleEditConfirm}
        isPending={false}
        title={`Edit ${selectedStudy?.type}`}
        okText="Continue to Edit"
        noText="Cancel"
        initialState={{ pinnedColumns: { right: ['actions'] } }}
        content={
          <Box>
            <Typography>
              Are you sure you want to edit this study? If you make any changes to the questions,
              the response before this edit will not be reflected in the new analysis. However, you
              are free to make other configuration changes.
            </Typography>
          </Box>
        }
      />

      {/* Duplicate Confirmation Dialog */}
      <ConfirmationDialog
        open={showDuplicateConfirm}
        onClose={handleDuplicateCancel}
        onConfirm={handleDuplicateConfirm}
        isPending={isDuplicatingSurvey || isDuplicatingInterview}
        title={`Duplicate ${duplicateStudyType || 'Study'}`}
        okText={isDuplicatingSurvey || isDuplicatingInterview ? 'Processing...' : 'Duplicate'}
        noText="Cancel"
        content={
          <Box>
            <Typography>
              Are you sure you want to create a duplicate of this{' '}
              {duplicateStudyType?.toLowerCase() || 'study'}? This will create a new copy with the
              same questions and settings.
            </Typography>
          </Box>
        }
      />
    </Box>
  );
}
